import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { emailRegex, FormState } from '@opu/utils';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AxiosInstance from '../../helpers/AxiosInstance';
import moment from 'moment';

const useForm = () => {
  const [year, setYear] = useState(new Date(Date.now()).getFullYear());

  const [headerStatistics, setHeaderStatistics] = useState({
    loading: false,
    establishmentsCount: 0,
    todaysConnectedStudentsCount: 0,
    booksCount: 0,
    bookConsultationsCount: 0,
    todaysBookConsultationsCount: 0,
    studentsCountPerGender: { data: [0, 0], labels: ['M', 'F'] },
    studentsCountPerLevel: { data: [], labels: [] },
    studentsCountPerActivationStatus: { false: 0, true: 0 },
  });

  const getStatistics = async () => {
    setHeaderStatistics({ ...headerStatistics, loading: true });
    const generalStatistics = await AxiosInstance.get('/statistics/');
    setHeaderStatistics({ ...headerStatistics, loading: false });

    setHeaderStatistics(generalStatistics);
  };

  const [connectedStudents, setConnectedStudents] = useState({
    loading: false,
    countPerDayInMonth: {
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      labels: [
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
        '16',
        '17',
        '18',
        '19',
        '20',
        '21',
        '22',
        '23',
        '24',
        '25',
        '26',
        '27',
        '28',
        '29',
        '30',
        '31',
      ],
    },
    countPerMonthInYear: {
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      labels: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
    },
    countPerYearForLastXYears: {
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      labels: [],
    },
  });

  const [connectedStudentsControls, setConnectedStudentsControls] = useState({
    loading: false,
    date: new Date().toDateString(),
    displayBy: 'day',
  });

  const getConnectedStudents = async (date) => {
    setConnectedStudentsControls({ ...connectedStudentsControls, loading: true });

    const stats = await AxiosInstance.get(`/statistics/connected-students?date=${date}`);
    setConnectedStudentsControls({ ...connectedStudentsControls, loading: false });

    setConnectedStudents(stats);
  };

  useEffect(() => {
    getConnectedStudents(connectedStudentsControls.date);
  }, [connectedStudentsControls.date]);

  const [createdStudents, setCreatedStudents] = useState({
    countPerDayInMonth: {
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      labels: [
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
        '16',
        '17',
        '18',
        '19',
        '20',
        '21',
        '22',
        '23',
        '24',
        '25',
        '26',
        '27',
        '28',
        '29',
        '30',
        '31',
      ],
    },
    countPerMonthInYear: {
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      labels: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
    },
    countPerYearForLastXYears: {
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      labels: [],
    },
  });

  const [createdStudentsControls, setCreatedStudentsControls] = useState({
    loading: false,
    date: new Date().toDateString(),
    displayBy: 'day',
  });

  const getCreatedStudents = async (date) => {
    setCreatedStudentsControls({ ...createdStudentsControls, loading: true });
    const stats = await AxiosInstance.get(`/statistics/created-students?date=${date}`);
    setCreatedStudentsControls({ ...createdStudentsControls, loading: false });

    setCreatedStudents(stats);
  };

  useEffect(() => {
    getCreatedStudents(createdStudentsControls.date);
  }, [createdStudentsControls.date]);

  const [todaysTopTenBooks, setTodaysTopTenBooks] = useState([]);
  const [selectedMonthTopTenBooks, setSelectedMonthTopTenBooks] = useState([]);
  const [selectedYearTopTenBooks, setSelectedYearTopTenBooks] = useState([]);

  const [booksControls, setBooksControls] = useState({
    loading: true,
    displayBy: 'day', //"month","year"
    day: true,
    monthYear: { month: moment(new Date()).month(), year: moment(new Date()).year() },
    year: moment(new Date()).year(),
  });

  const getTodaysTopTenBooks = async () => {
    setBooksControls({ ...booksControls, loading: true });
    const todaysTopTenBooks = await AxiosInstance.get(`/statistics/top-ten-books-today`);
    setBooksControls({ ...booksControls, loading: false });

    setTodaysTopTenBooks(todaysTopTenBooks);
  };

  const getMonthTopTenBooks = async (monthYear) => {
    const selectedMonthTopTenBooks = await AxiosInstance.get(
      `/statistics/top-ten-books?month=${monthYear.month + 1}&year=${monthYear.year}`
    );
    setSelectedMonthTopTenBooks(selectedMonthTopTenBooks);
  };

  useEffect(() => {
    getMonthTopTenBooks(booksControls.monthYear);
  }, [booksControls.monthYear]);

  const getYearTopTenBooks = async (year) => {
    const selectedYearTopTenBooks = await AxiosInstance.get(`/statistics/top-ten-books?year=${year}`);
    setSelectedYearTopTenBooks(selectedYearTopTenBooks);
  };

  useEffect(() => {
    getYearTopTenBooks(booksControls.year);
  }, [booksControls.year]);

  useEffect(() => {
    getStatistics();
    getTodaysTopTenBooks();

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 10; year <= currentYear; year++) {
      years.push(year);
    }
    setConnectedStudents({ ...connectedStudents, countPerYearForLastXYears: { data: [], labels: years } });
    setCreatedStudents({ ...createdStudents, countPerYearForLastXYears: { data: [], labels: years } });
  }, []);

  const parseBookNbrViews = (nbrViews) => {
    return nbrViews < 1000 ? nbrViews : `${parseInt(nbrViews / 1000)}K`;
  };

  return {
    headerStatistics,
    connectedStudents,
    connectedStudentsControls,
    setConnectedStudentsControls,
    createdStudents,
    createdStudentsControls,
    setCreatedStudentsControls,
    todaysTopTenBooks,
    selectedMonthTopTenBooks,
    selectedYearTopTenBooks,
    booksControls,
    setBooksControls,
    year,
    setYear,
    parseBookNbrViews,
  };
};

export default useForm;
